import CodeIcon from '@mui/icons-material/Code';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const escapeHtml = (value) =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const matchesSearch = (item, search) => {
    if (!search) return true;
    const haystack = `${item.sourceExcerpt || ''} ${item.translatedExcerpt || ''} ${item.reasons.join(' ')}`.toLowerCase();
    return haystack.includes(search.toLowerCase());
};

/** Builds a standalone, self-styled HTML document string for the "Save as HTML" export. */
const buildStandaloneReportHtml = ({ title, subtitle, pages }) => {
    const pagesHtml = pages
        .map((page) => {
            const itemsHtml = page.items
                .map((item) => {
                    const reasonsHtml = item.reasons.map((reason) => `<li>${escapeHtml(reason)}</li>`).join('');
                    const preview = item.translatedExcerpt || item.sourceExcerpt || '';
                    return `
                        <div class="item">
                            ${preview ? `<div class="excerpt">"${escapeHtml(preview)}"</div>` : ''}
                            <ul class="reasons">${reasonsHtml}</ul>
                        </div>`;
                })
                .join('');
            return `<section class="page"><h2>Page ${page.pageNumber}</h2>${itemsHtml}</section>`;
        })
        .join('');

    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<title>${escapeHtml(title)}</title>
<style>
    body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; max-width: 800px; margin: 32px auto; padding: 0 16px; color: #222; }
    h1 { font-size: 20px; margin-bottom: 4px; }
    .subtitle { color: #666; font-size: 13px; margin-bottom: 24px; }
    .page { margin-bottom: 20px; }
    .page h2 { font-size: 15px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
    .item { margin: 8px 0; padding: 10px; border: 1px solid #eee; border-radius: 6px; }
    .excerpt { font-style: italic; color: #555; margin-bottom: 6px; }
    .reasons { margin: 0; padding-left: 18px; font-size: 13px; }
    @media print { body { margin: 0; } }
</style>
</head>
<body>
<h1>${escapeHtml(title)}</h1>
<div class="subtitle">${escapeHtml(subtitle)}</div>
${pagesHtml || '<p>No items flagged.</p>'}
</body>
</html>`;
};

const downloadBlob = (blob, filename) => {
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
};

/**
 * Dedicated review-report dialog shown before exporting an IDML translation (design doc §4/§6:
 * overflow/estimated-placement/unreviewed items are flagged, never silently fixed).
 *
 * Replaces the earlier raw Swal confirm: adds search/filter-by-reason so a long flagged list is
 * actually navigable, and lets the report be saved as a standalone HTML file or a PDF (via
 * html2canvas + jsPDF, snapshotting the currently-filtered view) for sharing with a reviewer who
 * doesn't have editor access.
 */
const IdmlReviewReportDialog = ({ open, report, bookName, languageCode, onClose, onExportAnyway }) => {
    const [search, setSearch] = useState('');
    const [reasonFilter, setReasonFilter] = useState('all');
    const [saving, setSaving] = useState(null);
    const contentRef = useRef(null);

    const reasonOptions = useMemo(() => {
        if (!report) return [];
        const set = new Set();
        report.pages.forEach((page) => page.items.forEach((item) => item.reasons.forEach((r) => set.add(r))));
        return Array.from(set);
    }, [report]);

    const filteredPages = useMemo(() => {
        if (!report) return [];
        return report.pages
            .map((page) => ({
                ...page,
                items: page.items.filter(
                    (item) =>
                        matchesSearch(item, search) && (reasonFilter === 'all' || item.reasons.includes(reasonFilter)),
                ),
            }))
            .filter((page) => page.items.length > 0);
    }, [report, search, reasonFilter]);

    const filteredCount = useMemo(() => filteredPages.reduce((sum, page) => sum + page.items.length, 0), [filteredPages]);

    if (!report) return null;

    const title = `${report.totalFlagged} item(s) flagged for manual review`;
    const subtitle = `${bookName || 'Book'} — ${languageCode}. These translations weren't auto-adjusted to fit. The exported .idml's format, fonts, and sizing are untouched. Review these pages in InDesign and make any layout changes there.`;

    const handleSaveHtml = () => {
        const html = buildStandaloneReportHtml({ title, subtitle, pages: filteredPages });
        downloadBlob(new Blob([html], { type: 'text/html' }), `${bookName || 'book'}-${languageCode}-review-report.html`);
    };

    const handleSavePdf = async () => {
        const node = contentRef.current;
        if (!node) return;
        setSaving('pdf');
        // The on-screen container is a fixed-height scroll area (maxHeight + overflowY:'auto'), and
        // html2canvas snapshots the painted viewport rather than the full scrollHeight, so without
        // this it would only capture whatever portion happened to be scrolled into view. Temporarily
        // expand the container to its full content height for the capture, then restore it.
        const previousMaxHeight = node.style.maxHeight;
        const previousOverflowY = node.style.overflowY;
        node.style.maxHeight = 'none';
        node.style.overflowY = 'visible';
        try {
            const canvas = await html2canvas(node, { scale: 2, backgroundColor: '#ffffff' });
            const pdf = new jsPDF('p', 'pt', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pageWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            const pxPerPdfPt = canvas.height / imgHeight;
            const sliceHeightPx = pageHeight * pxPerPdfPt;

            let renderedPx = 0;
            let isFirstPage = true;
            while (renderedPx < canvas.height) {
                const thisSliceHeightPx = Math.min(sliceHeightPx, canvas.height - renderedPx);
                const sliceCanvas = document.createElement('canvas');
                sliceCanvas.width = canvas.width;
                sliceCanvas.height = thisSliceHeightPx;
                sliceCanvas
                    .getContext('2d')
                    .drawImage(canvas, 0, renderedPx, canvas.width, thisSliceHeightPx, 0, 0, canvas.width, thisSliceHeightPx);

                if (!isFirstPage) pdf.addPage();
                isFirstPage = false;
                pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, thisSliceHeightPx / pxPerPdfPt);
                renderedPx += thisSliceHeightPx;
            }

            pdf.save(`${bookName || 'book'}-${languageCode}-review-report.pdf`);
        } catch (err) {
            console.error('Failed to save review report as PDF:', err);
        } finally {
            node.style.maxHeight = previousMaxHeight;
            node.style.overflowY = previousOverflowY;
            setSaving(null);
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" alignItems="center" gap={1}>
                    <WarningAmberIcon color="warning" fontSize="small" />
                    <Typography variant="h6" component="span">
                        {title}
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {subtitle}
                </Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                <Stack direction="row" gap={1} sx={{ p: 2, pb: 1 }} flexWrap="wrap">
                    <TextField
                        size="small"
                        placeholder="Search flagged items..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: 220, flexGrow: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Select size="small" value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)} sx={{ minWidth: 220 }}>
                        <MenuItem value="all">All reasons</MenuItem>
                        {reasonOptions.map((reason) => (
                            <MenuItem key={reason} value={reason} sx={{ whiteSpace: 'normal' }}>
                                {reason.length > 60 ? `${reason.slice(0, 60)}...` : reason}
                            </MenuItem>
                        ))}
                    </Select>
                    <Chip size="small" label={`Showing ${filteredCount} of ${report.totalFlagged}`} />
                </Stack>

                <Box ref={contentRef} sx={{ maxHeight: 420, overflowY: 'auto', px: 2, pb: 2, bgcolor: 'background.paper' }}>
                    {filteredPages.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No flagged items match this filter.
                        </Typography>
                    ) : (
                        filteredPages.map((page) => (
                            <Box key={page.pageNumber} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Page {page.pageNumber}
                                </Typography>
                                <Stack gap={1}>
                                    {page.items.map((item, idx) => (
                                        <Box key={`${page.pageNumber}-${idx}`} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                            {(item.translatedExcerpt || item.sourceExcerpt) && (
                                                <Typography variant="body2" fontStyle="italic" color="text.secondary" sx={{ mb: 0.5 }}>
                                                    "{item.translatedExcerpt || item.sourceExcerpt}"
                                                </Typography>
                                            )}
                                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                                {item.reasons.map((reason, rIdx) => (
                                                    <Typography key={rIdx} component="li" variant="caption">
                                                        {reason}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                                <Divider sx={{ mt: 1.5 }} />
                            </Box>
                        ))
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5 }}>
                <Stack direction="row" gap={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CodeIcon fontSize="small" />}
                        onClick={handleSaveHtml}
                        disabled={Boolean(saving)}
                    >
                        Save as HTML
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PictureAsPdfIcon fontSize="small" />}
                        onClick={handleSavePdf}
                        disabled={Boolean(saving)}
                    >
                        {saving === 'pdf' ? 'Saving...' : 'Save as PDF'}
                    </Button>
                </Stack>
                <Stack direction="row" gap={1}>
                    <Button onClick={() => onClose(false)}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={() => onExportAnyway()}>
                        Export anyway
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default IdmlReviewReportDialog;
